(ns manage.ad-variant.creation.core.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Tabs$default" :as Tabs]
            ["src/svg/controls/scope.svg$default" :as Scope]
            ["src/svg/files_folders/file-text.svg$default" :as FileText]
            ["src/svg/files_folders/image-stack.svg$default" :as ImageStack]
            ["src/svg/objects/rectangle-split-top-and-left.svg$default" :as RectangleSplitTopAndLeft]
            [common.flags :refer [use-flag]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.util :as util]
            [common.widgets.overlay-page :as overlay-page]
            [common.widgets.page :as page]
            [manage.ad-variant.creation.ad-builder.view :refer [ad-builder-view]]
            [manage.ad-variant.creation.content.hooks :as h]
            [manage.ad-variant.creation.content.view :refer [content-view]]
            [manage.ad-variant.creation.core.exit-modal]
            [manage.ad-variant.creation.feed.mapping.hooks :as mh]
            [manage.ad-variant.creation.feed.mapping.incomplete-mapping-modal]
            [manage.ad-variant.creation.feed.view :refer [feed-view]]
            [manage.ad-variant.creation.strategy.view :refer [strategy-view]]
            [manage.ad-variant.creation.template.view :refer [template-view]]
            [manage.ad-variant.queries :as q]
            [manage.adset.hooks :refer [use-synced-with-hogarth?]]
            [manage.content-variant-page.queries :as content-queries]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui content [{:keys [mode feed-id content-variants-count channel]}]
  (let [{:keys [advertiser-id] :as url-context} (use-subscribe [:url-context])

        {{{:keys [latest-ad-variants-updated-at]} :data} :data} (use-query (q/adset-content-build-query url-context))

        {trigger-feed-import :mutate} (use-mutation (content-queries/trigger-feed-import-mutation url-context feed-id))
        {trigger-ad-variants :mutate} (use-mutation (q/trigger-ad-variants-mutation url-context feed-id))

        unapplied-changes (h/use-unapplied-changes)
        setup-complete? (h/use-setup-complete?)
        mapping-complete? (mh/use-everything-mapped?)
        can-safely-trigger-feed-import? (h/use-can-safely-trigger-feed-import? {:advertiser-id advertiser-id :feed-id feed-id})

        production-studio-adset? (use-synced-with-hogarth?)
        template-tab-available? (not (and (= channel "meta") production-studio-adset?))
        ad-builder-creation-enabled? (use-flag :ad-builder-creation)
        tabs (uix/use-memo
              (fn []
                (remove nil?
                        [(when template-tab-available?
                           (if ad-builder-creation-enabled?
                             {:key :ad-builder
                              :label "Ad builders"
                              :icon ($ RectangleSplitTopAndLeft)
                              :content ($ ad-builder-view)
                              :complete (:template setup-complete?)}
                             {:key :template
                              :label "Template"
                              :icon ($ RectangleSplitTopAndLeft)
                              :content ($ template-view)
                              :complete (:template setup-complete?)}))
                         {:key :feed
                          :label "Feed"
                          :icon ($ FileText)
                          :content ($ feed-view)
                          :complete (and (:feed setup-complete?)
                                         (:placeholder-mapping setup-complete?))}
                         {:key :strategy
                          :label "Strategy"
                          :icon ($ Scope)
                          :content ($ strategy-view)
                          :complete (and (:feed setup-complete?)
                                         (:placeholder-mapping setup-complete?))}
                         {:key :content
                          :label ($ :<>
                                    "Content"
                                    (when (pos? content-variants-count)
                                      ($ Chip
                                         {:sx #js {:ml 1}
                                          :variant "subtle"
                                          :label content-variants-count})))
                          :icon ($ ImageStack)
                          :content ($ content-view)}]))
              [channel content-variants-count setup-complete? template-tab-available? ad-builder-creation-enabled?])

        tabs* (->> (map (fn [tab prev next]
                          (assoc tab
                                 :previous-key (:key prev)
                                 :next-key (:key next)))
                        tabs
                        (concat [nil] tabs)
                        (concat (rest tabs) [nil]))
                   (util/index-by :key))


        ;; Store the active tab to URL so browser history remembers it
        active-tab (:tab (use-subscribe [:router/query-parameters])
                         (if template-tab-available?
                           (if ad-builder-creation-enabled? :ad-builder :template)
                           :feed))
        set-active-tab (uix/use-callback (fn [tab]
                                           (rfe/set-query #(assoc % :tab tab))
                                           ;; Trigger content import (not ad-variant creation)
                                           ;; when navigating to the "Content" tab (via tab click or the Next button),
                                           ;; provided the content can be updated safely. See: `h/use-can-safely-trigger-feed-import?`
                                           (when (and feed-id (= tab :content) can-safely-trigger-feed-import?)
                                             (trigger-feed-import)))
                                         [feed-id can-safely-trigger-feed-import? trigger-feed-import])

        active-tab-props (get tabs* active-tab)

        previous-tab (get-in tabs* [active-tab :previous-key])
        next-tab (get-in tabs* [active-tab :next-key])
        skippable? (get-in tabs* [active-tab :skippable?])]
    ($ page/content-padding
       {:sx #js {:pt 3
                 :gap 1
                 :overflow "hidden"}}
       ($ Stack {:direction "row"
                 :justifyContent "space-between"
                 :alignItems "center"}
          ($ Tabs {:value active-tab
                   :onChange (fn [_ tab]
                               (set-active-tab (keyword tab)))
                   :sx #js {".MuiTabs-flexContainer" #js {:gap 2}}}
             (for [[key {:keys [label icon] :as tab}] tabs*
                   :let [previous (get tabs* (:previous-key tab))]]
               ($ Tab {:key key
                       :value key
                       :label label
                       :icon ($ SvgIcon icon)
                       :iconPosition "start"
                       :disableRipple true
                       :disabled (not (:complete previous true))
                       :sx #js {:px 0
                                :py 2
                                :minWidth "unset"
                                :minHeight "unset"}})))
          ($ Stack {:direction "row"
                    :spacing 1}
             (if (= mode :create)
               ($ :<>
                  (when previous-tab
                    ($ Button
                       {:variant "text"
                        :onClick #(set-active-tab previous-tab)
                        :sx #js {:color "grey.10"
                                 :fontWeight 500}}
                       "Back"))
                  (when next-tab
                    ($ Button
                       {:variant "contained"
                        :disabled (not (:complete active-tab-props))
                        :onClick (fn []
                                   (let [next-fn #(set-active-tab next-tab)]
                                     (if (and (not mapping-complete?) (= next-tab :content))
                                       (rf/dispatch [:modal/open {:id :ad-variant/placeholders-are-not-mapped
                                                                  :props {:on-confirm next-fn}}])
                                       (next-fn))))}
                       (if skippable? "Skip" "Next"))))
               ($ Button
                  {:variant "contained"
                   :disabled (->> unapplied-changes vals (every? false?))
                   :onClick (fn []
                              (let [trigger-fn #(do (if can-safely-trigger-feed-import?
                                                      (trigger-feed-import {:trigger-ad-variants? true})
                                                      (trigger-ad-variants))
                                                    (rfe/navigate :adset/ad-variants {:path-params url-context}))]
                                (if mapping-complete?
                                  (trigger-fn)
                                  (rf/dispatch [:modal/open {:id :ad-variant/placeholders-are-not-mapped
                                                             :props {:on-confirm trigger-fn}}]))))}
                  (if (some? latest-ad-variants-updated-at)
                    "Update ad variants"
                    "Generate ad variants")))))
       (get-in tabs* [active-tab :content]))))

(def ad-variant-creation-page
  "This is used with Fade transition in the parent page, so the
   component needs to support ref and use given style to
   apply the Fade transition styles."
  (uix/as-react
   (fn [{:keys [ref style]}]
     (let [{:keys [advertiser-id adset-id] :as url-context} (use-subscribe [:url-context])
           {{feed-id :feedId channel :channel} :data} (use-query (adset-queries/adset-query {:adset-id adset-id}))

           latest-import-status-response (use-query (content-queries/latest-feed-import-status-query advertiser-id feed-id))
           content-variants-count (:variants-after-import (:data (:data latest-import-status-response)))
           import-id (-> latest-import-status-response :data :data :id)

           mode (if import-id :edit :create)

           setup-complete? (h/use-setup-complete?)
           unapplied-changes (h/use-unapplied-changes)

           ;; FIXME: Consider simpler way to return to the parent page?
           ;; If creation is a query param, it could be simpler to toggle the creation overlay.
           close-creation (uix/use-callback (fn close-creation []
                                              (letfn [(navigate-out []
                                                        (rfe/navigate :adset/ad-variants {:path-params url-context}))]
                                                (cond
                                                  (and (= mode :create) (->> setup-complete? vals (some false?)))
                                                  (rf/dispatch [:modal/open {:id :ad-variant/setup-incomplete
                                                                             :props {:close-creation navigate-out
                                                                                     :setup-complete? setup-complete?}}])

                                                  (and (= mode :edit) (->> unapplied-changes vals (some true?)))
                                                  (rf/dispatch [:modal/open {:id :ad-variant/unapplied-changes
                                                                             :props {:close-creation navigate-out
                                                                                     :unapplied-changes unapplied-changes
                                                                                     :feed-id feed-id}}])

                                                  :else (navigate-out))))
                                            [url-context setup-complete? unapplied-changes mode feed-id])]
       ($ overlay-page/overlay-page
          {:ref ref
           :style style}
          ($ overlay-page/header {:on-close close-creation :title "Create ad variants"}
             ($ Stack
                {:direction "row"
                 :alignItems "center"
                 :spacing 1}
                ($ Button
                   {:variant "text"
                    :onClick close-creation
                    :sx #js {:color "grey.10"
                             :fontWeight 500}}
                   "Close")))
          ($ content
             {:feed-id feed-id
              :channel channel
              :content-variants-count content-variants-count
              :mode mode}))))))
